import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { FaSearch, FaPhone, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import FormInput from '../common/FormInput';
import Button from '../common/Button';
import Alert from '../common/Alert';
import CommunityIdentity from '../common/CommunityIdentity';
import useApi from '../../hooks/useApi';
import { toast } from 'react-toastify';
import 'animate.css';
import 'bootstrap/dist/css/bootstrap.min.css';

// MemberCard component for rendering individual member details
const MemberCard = React.memo(({ member, isSuperAdmin }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className="member-card animate__animated animate__fadeInUp">
      <div className="member-card-header" onClick={toggleExpand}>
        <div>
          <h6 className="mb-0 text-dark">{member.name}</h6>
          <small className="text-muted">{member.ownershipType || 'N/A'}</small>
        </div>
        <div className="d-flex align-items-center gap-2">
          {isExpanded ? (
            <FaChevronUp size={14} className="text-muted" />
          ) : (
            <FaChevronDown size={14} className="text-muted" />
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="member-card-body">
          <div className="mb-2">
            <strong className="text-dark">Gender:</strong>{' '}
            <span className="text-muted">{member.gender || '-'}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Blood Group:</strong>{' '}
            <span className="text-muted">{member.bloodGroup || '-'}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Age:</strong>{' '}
            <span className="text-muted">{member.age || '-'}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Birthdate:</strong>{' '}
            <span className="text-muted">{member.birthdate || '-'}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Mobile No:</strong>{' '}
            <span className="contact-info">
              <span>{member.mobileNo || '-'}</span>
              {member.mobileNo && (
                <a href={`tel:${member.mobileNo}`} className="ms-1">
                  <FaPhone size={14} className="phone-icon" />
                </a>
              )}
            </span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Relation:</strong>{' '}
            <span className="text-muted">{member.relation || '-'}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">House No:</strong>{' '}
            <span className="text-muted">{member.houseNo || '-'}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Division:</strong>{' '}
            <span className="text-muted">{member.division || '-'}</span>
          </div>
          {isSuperAdmin && (
            <div className="mb-2">
              <strong className="text-dark">Community:</strong>{' '}
              {member.community ? <CommunityIdentity community={member.community} size="xs" compact /> : 'N/A'}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

function SearchMembers({ isSuperAdmin }) {
  const { session } = useContext(AuthContext);
  const { communityId } = session || {};
  const { get } = useApi();
  const [searchParams, setSearchParams] = useState({
    name: '',
    division: '',
    houseNo: '',
    bloodGroup: '',
    communityId: isSuperAdmin ? '' : communityId,
  });
  const [members, setMembers] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoadingCommunities, setIsLoadingCommunities] = useState(false);
  const [error, setError] = useState('');
  const isMounted = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fetch communities for super admin
  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetchCommunities = async () => {
      setIsLoadingCommunities(true);
      try {
        console.log('Fetching communities from /api/communities/active');
        const response = await get('/api/communities/active');
        const communityList = Array.isArray(response.data) ? response.data : Array.isArray(response) ? response : [];
        console.log('Communities response:', communityList);
        if (isMounted.current) {
          setCommunities(communityList);
          setError('');
        }
      } catch (err) {
        console.error('Fetch communities error:', err);
        if (isMounted.current) {
          setError('Failed to load communities.');
          toast.error('Failed to load communities.');
        }
      } finally {
        if (isMounted.current) {
          setIsLoadingCommunities(false);
        }
      }
    };
    fetchCommunities();
  }, [isSuperAdmin, get]);

  // Fetch members with filters
  const fetchMembers = useCallback(async (page = 0) => {
    try {
      const params = {
        name: searchParams.name.trim() || undefined,
        division: searchParams.division.trim() || undefined,
        houseNo: searchParams.houseNo.trim() || undefined,
        bloodGroup: searchParams.bloodGroup.trim() || undefined,
        communityId: isSuperAdmin && searchParams.communityId ? searchParams.communityId : undefined,
        page,
        size: 10,
      };
      console.log('Fetching members with params:', params);
      const response = await get('/api/members/search', { params });
      console.log('Members response:', response);
      if (!response.content || !Array.isArray(response.content)) {
        throw new Error('Invalid members response format');
      }
      if (isMounted.current) {
        const uniqueMembers = [];
        const seenIds = new Set();
        response.content.forEach((member) => {
          if (!seenIds.has(member.id)) {
            seenIds.add(member.id);
            uniqueMembers.push(member);
          }
        });
        setMembers(uniqueMembers);
        setTotalPages(response.totalPages || 0);
        setError('');
      }
    } catch (err) {
      console.error('Fetch members error:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      if (isMounted.current) {
        const errorMessage =
          err.response?.status === 403
            ? 'You do not have permission to search this community.'
            : err.response?.status === 404
            ? 'Members endpoint not found. Please check API configuration.'
            : 'Failed to fetch members. Please try again.';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    }
  }, [searchParams, isSuperAdmin, communityId, get]);

  // Trigger fetch on filter or page change
  useEffect(() => {
    fetchMembers(currentPage);
  }, [searchParams, currentPage, fetchMembers]);

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchMembers(0);
  };

  // Handle filter input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchParams((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(0);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchParams({
      name: '',
      division: '',
      houseNo: '',
      bloodGroup: '',
      communityId: isSuperAdmin ? '' : communityId,
    });
    setCurrentPage(0);
    setError('');
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="container py-4 animate__animated animate__fadeIn">
      <h2 className="mb-4 fs-4 fw-bold text-dark">Search Members</h2>
      <div className="card shadow-sm mb-4 border-0">
        <div className="card-body">
          <h5 className="card-title fs-5 mb-3 text-dark">Filter Members</h5>
          {error && <Alert type="danger" message={error} />}
          <form onSubmit={handleSearch}>
            <div className="row g-3">
              <div className="col-md-2 col-sm-6">
                <FormInput
                  label="Name"
                  name="name"
                  value={searchParams.name}
                  onChange={handleChange}
                  placeholder="Enter name"
                />
              </div>
              <div className="col-md-2 col-sm-6">
                <FormInput
                  label="Division"
                  name="division"
                  value={searchParams.division}
                  onChange={handleChange}
                  placeholder="Enter division"
                />
              </div>
              <div className="col-md-2 col-sm-6">
                <FormInput
                  label="House No"
                  name="houseNo"
                  value={searchParams.houseNo}
                  onChange={handleChange}
                  placeholder="Enter house number"
                />
              </div>
              <div className="col-md-2 col-sm-6">
                <FormInput
                  label="Blood Group"
                  name="bloodGroup"
                  value={searchParams.bloodGroup}
                  onChange={handleChange}
                  placeholder="e.g., A_POSITIVE"
                  list="bloodGroups"
                />
                <datalist id="bloodGroups">
                  <option value="A_POSITIVE" />
                  <option value="A_NEGATIVE" />
                  <option value="B_POSITIVE" />
                  <option value="B_NEGATIVE" />
                  <option value="AB_POSITIVE" />
                  <option value="AB_NEGATIVE" />
                  <option value="O_POSITIVE" />
                  <option value="O_NEGATIVE" />
                </datalist>
              </div>
              {isSuperAdmin && (
                <div className="col-md-2 col-sm-6">
                  <div className="mb-3">
                    <label htmlFor="communityId" className="form-label">Community</label>
                    {isLoadingCommunities ? (
                      <div>Loading communities...</div>
                    ) : communities.length === 0 ? (
                      <div>No communities available</div>
                    ) : (
                      <select
                        className="form-select"
                        id="communityId"
                        name="communityId"
                        value={searchParams.communityId}
                        onChange={handleChange}
                      >
                        <option value="">All Communities</option>
                        {communities.map((community) => (
                          <option key={community.id} value={community.id}>
                            {community.displayName}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              )}
              <div className="col-md-2 col-sm-6 d-flex align-items-end gap-2">
                <Button type="submit" variant="primary" className="flex-fill">
                  <FaSearch /> Search
                </Button>
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={clearFilters}
                  className="flex-fill"
                >
                  Clear
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
      {members.length === 0 ? (
        <Alert type="info" message="No members found." />
      ) : (
        <>
          <style>
            {`
              .member-card {
                border: none;
                border-radius: 12px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
                transition: transform 0.2s, box-shadow 0.2s;
                background-color: #fff;
                overflow: hidden;
              }
              .member-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
              }
              .member-card-header {
                background-color: #f8f9fa;
                padding: 12px 16px;
                border-bottom: none;
                display: flex;
                justify-content: space-between;
                align-items: center;
                cursor: pointer;
              }
              .member-card-body {
                padding: 16px;
                background-color: #fff;
              }
              .member-grid {
                display: flex;
                flex-wrap: wrap;
                gap: 16px;
              }
              .member-card {
                flex: 1 1 300px;
                max-width: 300px;
              }
              @media (max-width: 576px) {
                .member-card {
                  flex: 1 1 100%;
                  max-width: 100%;
                }
              }
              .contact-info {
                display: inline-flex;
                align-items: center;
                gap: 6px;
              }
              .phone-icon {
                color: #28a745;
                cursor: pointer;
                transition: color 0.2s;
              }
              .phone-icon:hover {
                color: #218838;
              }
            `}
          </style>
          <div className="member-grid">
            {members.map((member) => (
              <MemberCard key={member.id} member={member} isSuperAdmin={isSuperAdmin} />
            ))}
          </div>
          {totalPages > 1 && (
            <>
              <nav className="mt-4">
                <ul className="pagination justify-content-center flex-wrap">
                  <li className={`page-item ${currentPage === 0 ? 'disabled' : ''}`}>
                    <Button
                      variant="link"
                      onClick={() => handlePageChange(currentPage - 1)}
                      className="page-link"
                      disabled={currentPage === 0}
                    >
                      Previous
                    </Button>
                  </li>
                  {[...Array(Math.min(totalPages, 5)).keys()].map((i) => {
                    const pageNum = i + Math.max(0, currentPage - 2);
                    if (pageNum >= totalPages) return null;
                    return (
                      <li
                        key={pageNum}
                        className={`page-item ${currentPage === pageNum ? 'active' : ''}`}
                      >
                        <Button
                          variant="link"
                          onClick={() => handlePageChange(pageNum)}
                          className="page-link"
                        >
                          {pageNum + 1}
                        </Button>
                      </li>
                    );
                  })}
                  <li className={`page-item ${currentPage >= totalPages - 1 ? 'disabled' : ''}`}>
                    <Button
                      variant="link"
                      onClick={() => handlePageChange(currentPage + 1)}
                      className="page-link"
                      disabled={currentPage >= totalPages - 1}
                    >
                      Next
                    </Button>
                  </li>
                </ul>
              </nav>
              <p className="text-center text-muted">
                Showing {members.length} of {totalPages * 10} members
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}

SearchMembers.propTypes = {
  isSuperAdmin: PropTypes.bool.isRequired,
};

MemberCard.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    gender: PropTypes.string,
    bloodGroup: PropTypes.string,
    age: PropTypes.number,
    birthdate: PropTypes.string,
    ownershipType: PropTypes.string,
    mobileNo: PropTypes.string,
    relation: PropTypes.string,
    houseNo: PropTypes.string,
    division: PropTypes.string,
    community: PropTypes.shape({
      id: PropTypes.number,
      displayName: PropTypes.string,
    }),
  }).isRequired,
  isSuperAdmin: PropTypes.bool.isRequired,
};

export default SearchMembers;
